import { motion } from "framer-motion";
import { Package, Truck, Award, Clock, CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { ROUTE_PATHS } from "@/lib/index";

const products = [
  {
    id: 1,
    nameAr: "كرواسون فرنسي",
    image: '/images/WhatsApp Image 2026-05-05 at 1.31.09 PM (2).jpeg',
    descriptionAr: "كرواسون طازج يومياً بطبقات مقرمشة وزبدة فاخرة",
  },
  {
    id: 2,
    nameAr: "معجنات دنماركية",
    image: '/images/danish_pastry_1_page-0001 (1).jpg',
    descriptionAr: "معجنات متنوعة بحشوات الفواكه والشوكولاتة",
  },
  {
    id: 3,
    nameAr: "مافن وكب كيك",
    image: IMAGES.DESSERTS_5,
    descriptionAr: "مافن طري بنكهات متعددة وكب كيك مزين",
  },
  {
    id: 4,
    nameAr: "كيكات وتورتات",
    image: '/images/cakes_tortes_1_page-0001.jpg',
    descriptionAr: "كيكات فاخرة وتورتات بتصاميم احترافية",
  },
  {
    id: 5,
    nameAr: "خبز وأرغفة",
    image: '/images/WhatsApp Image 2026-05-05 at 1.31.10 PM (4).jpeg',
    descriptionAr: "خبز طازج يومياً بأنواع متعددة",
  },
  {
    id: 6,
    nameAr: "معجنات مالحة",
    image: '/images/savory_pastry_1_page-0001.jpg',
    descriptionAr: "معجنات مالحة متنوعة للوجبات الخفيفة",
  },
];

const advantages = [
  {
    id: 1,
    icon: Truck,
    titleAr: "توصيل يومي طازج",
    descriptionAr: "نوصل منتجاتنا طازجة كل صباح في الوقت المحدد",
  },
  {
    id: 2,
    icon: Award,
    titleAr: "جودة ثابتة",
    descriptionAr: "معايير صارمة تضمن نفس الجودة العالية في كل مرة",
  },
  {
    id: 3,
    icon: Package,
    titleAr: "قائمة متجددة",
    descriptionAr: "نوفر خدمة التغليف المخصص بشعار كافيهك",
  },
  {
    id: 4,
    icon: Clock,
    titleAr: "كميات مرنة",
    descriptionAr: "نتكيف مع احتياجاتك اليومية بكميات مرنة",
  },
];

const steps = [
  {
    id: 1,
    number: "01",
    titleAr: "تواصل معنا",
    descriptionAr: "اتصل بنا أو أرسل طلبك عبر واتساب لمناقشة احتياجاتك",
  },
  {
    id: 2,
    number: "02",
    titleAr: "اختر منتجاتك",
    descriptionAr: "نساعدك في اختيار المنتجات المناسبة لكافيهك وعملائك",
  },
  {
    id: 3,
    number: "03",
    titleAr: "استلم يومياً",
    descriptionAr: "نبدأ التوريد اليومي في الوقت المحدد بجودة ثابتة",
  },
];

const galleryImages = [
  { id: 1, image: '/images/savory_pastry_1_page-0001.jpg', titleAr: "معجنات مالحة فاخرة" },
  { id: 2, image: IMAGES.CAFE_SUPPLY_4, titleAr: "معجنات متنوعة" },
  { id: 3, image: IMAGES.CAFE_SUPPLY_5, titleAr: "كرواسون طازج" },
  { id: 4, image: IMAGES.CAFE_SUPPLY_6, titleAr: "حلويات فاخرة" },
  { id: 5, image: IMAGES.CAFE_SUPPLY_7, titleAr: "معجنات يومية" },
  { id: 6, image: IMAGES.CAFE_SUPPLY_9, titleAr: "منتجات متميزة" },
  { id: 7, image: IMAGES.CAFE_SUPPLY_10, titleAr: "تشكيلة واسعة" },
  { id: 8, image: '/images/WhatsApp Image 2026-05-05 at 1.31.10 PM (4).jpeg', titleAr: "خبز طازج" },
];

export default function CafeSupply() {
  return (
    <div className="min-h-screen bg-background" dir="rtl">
        <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CAFE_SUPPLY_1}
              alt="توريد الكافيهات"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="relative z-10 container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
            >
              <Link
                to={ROUTE_PATHS.HOME}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>العودة للرئيسية</span>
              </Link>
              <h1 className="text-5xl lg:text-7xl font-bold text-foreground mb-6">
                شريكك المثالي للتوريد اليومي
              </h1>
              <p className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
                نوفر لكافيهك أجود المخبوزات والحلويات الطازجة يومياً بجودة ثابتة وأسعار تنافسية
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="#products"
                  className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all duration-300 hover:scale-105"
                >
                  تصفح المنتجات
                </a>
                <a
                  href="https://wa.me/966550096297"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border-2 border-accent text-accent rounded-xl font-semibold hover:bg-accent hover:text-accent-foreground transition-all duration-300 hover:scale-105"
                >
                  تواصل واتساب
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="products" className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
              >
                المنتجات التي نوردها
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                تشكيلة واسعة من المخبوزات والحلويات الطازجة لتلبية احتياجات كافيهك
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  variants={staggerItem}
                  className="group bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.nameAr}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {product.nameAr}
                    </h3>
                    <p className="text-muted-foreground">
                      {product.descriptionAr}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
              >
                لماذا نحن الخيار الأفضل
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                مزايا تجعلنا الشريك المثالي لكافيهك
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {advantages.map((advantage) => {
                const Icon = advantage.icon;
                return (
                  <motion.div
                    key={advantage.id}
                    variants={staggerItem}
                    className="bg-card rounded-2xl p-8 text-center shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-2"
                  >
                    <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">
                      {advantage.titleAr}
                    </h3>
                    <p className="text-muted-foreground">
                      {advantage.descriptionAr}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
              >
                معرض الأعمال
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                نماذج من منتجاتنا التي نوردها للكافيهات
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {galleryImages.map((item) => (
                <motion.div
                  key={item.id}
                  variants={staggerItem}
                  className="group relative aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                >
                  <img
                    src={item.image}
                    alt={item.titleAr}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                    <p className="text-primary-foreground font-semibold">
                      {item.titleAr}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
              >
                كيف نعمل معك
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                ثلاث خطوات بسيطة لبدء الشراكة
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            >
              {steps.map((step, index) => (
                <motion.div
                  key={step.id}
                  variants={staggerItem}
                  className="relative bg-card rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-500"
                >
                  <div className="absolute -top-6 right-8 w-12 h-12 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-bold text-xl">
                    {step.number}
                  </div>
                  <div className="mt-4">
                    <h3 className="text-2xl font-bold text-foreground mb-3">
                      {step.titleAr}
                    </h3>
                    <p className="text-muted-foreground">
                      {step.descriptionAr}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -left-4 w-8 h-0.5 bg-accent/30" />
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <CheckCircle className="w-16 h-16 mx-auto mb-6 text-accent" />
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                ابدأ الشراكة معنا اليوم
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                انضم إلى عشرات الكافيهات التي تثق بجودتنا والتزامنا
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to={ROUTE_PATHS.CONTACT}
                  className="px-8 py-4 bg-accent text-accent-foreground rounded-xl font-semibold hover:bg-accent/90 transition-all duration-300 hover:scale-105"
                >
                  تواصل معنا
                </Link>
                <a
                  href="https://wa.me/966550096297"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border-2 border-primary-foreground text-primary-foreground rounded-xl font-semibold hover:bg-primary-foreground hover:text-primary transition-all duration-300 hover:scale-105"
                >
                  واتساب مباشر
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
  );
}
